/**
 * Full-page FlexFlow form for BYO source creation.
 *
 * Wraps FlowParamsProvider + HeaderMetaConfigProvider + Form with the real
 * FlexFlow steps (Connect → Authenticate → Configure). "Configure" is
 * ByoSourceConfigureStep which uses FileSystem (folder browser, Folder Filters,
 * File Preview, schedule via MonitoringInterval) and on "Create" calls
 * buildByoSourcePayload so the code container is created alongside the source.
 *
 * isModal=false → Form takes the full viewport (position: fixed; 100vw × 100vh).
 */

import React, { useEffect, useRef } from 'react';
import useAppDispatch from 'js/hooks/useAppDispatch';
import { loadDataCredentials, loadVendors } from 'js/actions';
import { Form } from 'new/elements/Form';
import { FORM_TYPE_CREATE_DIRECT_FLOW } from 'js/constants/formTypes';
import { ConnectStep } from 'new/components/flows/types/DirectFlow/steps/ConnectStep';
import { AuthenticateStep } from 'new/components/flows/types/DirectFlow/steps/AuthenticateStep';
import { useDirectFlow } from 'new/components/flows/types/DirectFlow/hooks/useDirectFlow';
import { useStartSource } from 'new/components/flows/types/DirectFlow/hooks/useStartSource';
import { HeaderMetaConfigProvider } from 'new/components/flows/context/HeaderMetaConfigProvider';
import FlowParamsProvider from 'new/components/flows/FlowParamsProvider';
import { StudioDraftProvider } from '../studio/StudioDraftContext';
import { ByoSourceConfigureStep } from './ByoSourceConfigureStep';

interface ByoSourceSetupFormProps {
  onDismiss: (data?: any) => void;
  id?: number;
  isEditMode?: boolean;
}

function ByoSourceFormInner({ onDismiss, isEditMode }: ByoSourceSetupFormProps) {
  const start = useRef(true);
  const dispatch = useAppDispatch();
  const { onStartForm, updateFormData } = useDirectFlow();

  // SelectConnection shows "Loading connectors…" until vendors are in the store.
  // ConnectStep never dispatches loadVendors, so we do it here on mount.
  useEffect(() => {
    dispatch(loadVendors() as any);
    dispatch(loadDataCredentials() as any);
  }, [dispatch]);

  if (start.current) {
    onStartForm();
    updateFormData({ flowType: 'custom', connectionType: 'SOURCE' });
    start.current = false;
  }

  useStartSource();

  return (
    <HeaderMetaConfigProvider>
      <Form
        formType={FORM_TYPE_CREATE_DIRECT_FLOW}
        isModal={false}
        isEditMode={isEditMode ?? false}
        // In edit mode the source is already connected — open the Configure step
        // (index 2: Connect=0, Authenticate=1, Configure=2) with values prefilled
        // by useStartSource, mirroring ByoDestinationSetupForm.
        startPage={isEditMode ? 2 : undefined}
        onDismiss={() => {
          onDismiss();
          return true;
        }}
      >
        <ConnectStep />
        <AuthenticateStep />
        <ByoSourceConfigureStep />
      </Form>
    </HeaderMetaConfigProvider>
  );
}

export function ByoSourceSetupForm({ onDismiss, id, isEditMode }: ByoSourceSetupFormProps) {
  return (
    <StudioDraftProvider>
      <FlowParamsProvider
        flowType="custom"
        resourceType="source"
        isModal
        id={id}
        isEditMode={isEditMode}
        onDismiss={onDismiss}
      >
        <ByoSourceFormInner onDismiss={onDismiss} isEditMode={isEditMode} />
      </FlowParamsProvider>
    </StudioDraftProvider>
  );
}

export default ByoSourceSetupForm;
